import { FeedbackVariants } from '@indigo-ag/component-module/dist/commonjs/shared/theme/theme-helpers/feedback';
import { BackgroundCheck, OpportunityContactRole } from '@indigo-ag/schema';
import React, { useContext } from 'react';
import { LoadingWrapper } from 'shared';
import { getCheckStatusTranslation } from '../BrazilGrowerFinance/utils/backgroundCheck.util';

import { CollapsibleContentCard, LinkButton, Status } from '@indigo-ag/component-module';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import { getContactById } from 'features/brazil-grower/queries/contact.queries';
import i18next from 'i18next';
import { Dictionary } from 'lodash';
import { useLocation } from 'react-router-dom';
import { GenericSalesforceLink } from 'shared/components/SalesforceLink/GenericSalesforceLink';
import styled from 'styled-components';
import {
  getFormattedTaxIdentifier,
  getOpportunityContactRoleType,
  getTranslatedRole
} from '../../pages/ContactDetailsPage/utils';
import { getCheckStatusVariant } from '../BrazilGrowerFinance/utils/backgroundCheck.util';
import { ContactDetailsGrid } from './ContactDetailsGrid';

export interface Props {
  opportunityContactRoles: OpportunityContactRole[];
  backgroundCheckMap: Dictionary<BackgroundCheck>;
}

const StyledCollapsibleContentCard = styled(CollapsibleContentCard)`
  margin-bottom: 16px;
`;

const LinksContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const BrazilGrowerContactsList: React.FunctionComponent<Props> = ({
  opportunityContactRoles,
  backgroundCheckMap
}) => {
  const { pathname } = useLocation();
  const { orderInfo } = useContext(FinanceProcessContext);
  const opportunityId = orderInfo?.opportunities?.[0].id;

  return (
    <>
      {opportunityContactRoles?.map(opportunityContactRole => {
        const contactId = opportunityContactRole?.contactId;
        const taxIdentifier = getFormattedTaxIdentifier(opportunityContactRole?.contact.taxIdentifier);
        const backgroundCheck = taxIdentifier ? backgroundCheckMap[taxIdentifier] : undefined;
        const backLinkLabel = encodeURI(i18next.t('backLinkLabel_OpportunityContacts') as string);

        return (
          <LoadingWrapper key={contactId} query={getContactById} queryOptions={{ variables: { contactId } }}>
            {({ data }) => {
              const contact = data.getContactById;
              const backgroundCheckStatus = backgroundCheck
                ? getCheckStatusTranslation(backgroundCheck.displayStatus)
                : i18next.t('backgroundCheckDisplayStatus_NotStarted');
              const displayContactRole = opportunityContactRole?.isPrimary
                ? i18next.t('primaryContact')
                : getTranslatedRole(
                    getOpportunityContactRoleType(opportunityContactRole?.role) ?? opportunityContactRole.role
                  );

              const title = `${contact.name} – ${displayContactRole}`;
              const statusVariant = backgroundCheck ? getCheckStatusVariant(backgroundCheck.displayStatus) : 'error';
              const profileUrl = `/br-growers/${opportunityId}/contacts/${contactId}?backLink=${pathname}&backLinkLabel=${backLinkLabel}`;

              return (
                <StyledCollapsibleContentCard
                  titleText={title}
                  initialCollapse={!opportunityContactRole?.isPrimary}
                  renderAfterTitle={() =>
                    backgroundCheckStatus ? (
                      <Status label={backgroundCheckStatus} variant={statusVariant as FeedbackVariants} />
                    ) : null
                  }
                  renderFooter={() => {
                    return (
                      <LinksContainer>
                        <LinkButton to={profileUrl} style={{ marginRight: 16, fontWeight: 'bold' }}>
                          {i18next.t('View Profile')}
                        </LinkButton>
                        <GenericSalesforceLink id={contactId} />
                      </LinksContainer>
                    );
                  }}
                >
                  <ContactDetailsGrid contact={contact} />
                </StyledCollapsibleContentCard>
              );
            }}
          </LoadingWrapper>
        );
      })}
    </>
  );
};
